// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/servicebus.
// An application's Service Bus sources: state at a glance, add, enable/pause.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Cable, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { ServiceBusSource } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { ModeBadge } from '@/components/ModeBadge'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { sourcesQuery, useDeleteSource, useUpdateSource } from '@/features/servicebus/api'
import { entityLabel, shortNamespace, SourceStateBadge } from '@/features/servicebus/lib'
import { SourceDialog } from '@/features/servicebus/SourceDialog'

export const Route = createFileRoute('/applications/$appId/service-bus/')({
  component: ServiceBusSourcesPage,
})

const col = tableColumns<ServiceBusSource>()

function buildColumns(
  h: {
    edit: (s: ServiceBusSource) => void
    remove: (s: ServiceBusSource) => void
    toggle: (s: ServiceBusSource, enabled: boolean) => void
  },
  canManage: boolean,
) {
  return col.columns([
    col.accessor('name', {
      header: 'Source',
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{c.getValue()}</div>
          {c.row.original.description && (
            <div className="truncate text-xs text-ink-3">{c.row.original.description}</div>
          )}
        </div>
      ),
      enableHiding: false,
    }),
    col.accessor('mode', {
      header: 'Mode',
      cell: (c) => <ModeBadge mode={c.getValue()} />,
      meta: { width: '5rem' },
    }),
    col.display({
      id: 'entity',
      header: 'Queue or subscription',
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-mono text-[13px] text-ink">
            {entityLabel(c.row.original)}
          </div>
          <div className="truncate font-mono text-xs text-ink-3">
            {shortNamespace(c.row.original.namespace)}
          </div>
        </div>
      ),
    }),
    col.accessor('format', {
      header: 'Format',
      cell: (c) => <Badge tone="outline">{c.getValue() === 'mapped' ? 'Mapped' : 'Envelope'}</Badge>,
      meta: { width: '6.5rem' },
    }),
    col.accessor('state', {
      header: 'State',
      cell: (c) => {
        const s = c.row.original
        const reason = s.state === 'error' ? s.last_error : s.state_reason
        return reason ? (
          <Tooltip content={reason}>
            <span>
              <SourceStateBadge source={s} />
            </span>
          </Tooltip>
        ) : (
          <SourceStateBadge source={s} />
        )
      },
      meta: { width: '6.5rem' },
    }),
    col.accessor('last_received_at', {
      header: 'Last message',
      cell: (c) =>
        c.getValue() ? (
          <RelativeTime value={c.getValue()!} className="text-ink-2" />
        ) : (
          <span className="text-ink-3">Never</span>
        ),
      sortFn: 'datetime',
      meta: { align: 'right', width: '8rem' },
    }),
    col.accessor('enabled', {
      header: 'Receive',
      cell: (c) =>
        canManage ? (
          <Switch
            aria-label={c.getValue() ? 'Stop receiving' : 'Start receiving'}
            checked={c.getValue()}
            onCheckedChange={(v) => h.toggle(c.row.original, v)}
          />
        ) : c.getValue() ? (
          <Badge tone="success">On</Badge>
        ) : (
          <Badge>Off</Badge>
        ),
      meta: { width: '6rem', stopRowClick: true },
    }),
    col.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: (c) =>
        canManage ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton label="Source actions" size="sm">
                <MoreHorizontal />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => h.edit(c.row.original)}>
                <Pencil /> Edit connection
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => h.remove(c.row.original)}>
                <Trash2 /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null,
      enableHiding: false,
      meta: { align: 'right', width: '3.5rem', stopRowClick: true, label: '' },
    }),
  ])
}

function ServiceBusSourcesPage() {
  const { appId } = Route.useParams()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const list = useQuery(sourcesQuery(appId))
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ServiceBusSource | undefined>()
  const [deleting, setDeleting] = useState<ServiceBusSource | undefined>()
  const del = useDeleteSource()
  const update = useUpdateSource()
  const canManage = can('manage_config')
  const setEnabled = update.mutate

  const open = (s: ServiceBusSource) =>
    void navigate({
      to: '/applications/$appId/service-bus/$sourceId',
      params: { appId, sourceId: s.id },
    })

  const columns = useMemo(
    () =>
      buildColumns(
        {
          edit: setEditing,
          remove: setDeleting,
          toggle: (s, enabled) => setEnabled({ id: s.id, body: { enabled } }),
        },
        canManage,
      ),
    [setEnabled, canManage],
  )

  return (
    <>
      <DataTable
        aria-label="Service Bus sources"
        columns={columns}
        data={list.data ?? []}
        getRowId={(s) => s.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={open}
        toolbar={
          <>
            <p className="text-sm text-ink-2">
              Queues and topic subscriptions MailSmith receives events from.
            </p>
            <div className="ml-auto">
              <Can permission="manage_config" mode="disable">
                <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                  Add source
                </Button>
              </Can>
            </div>
          </>
        }
        empty={
          <EmptyState
            icon={Cable}
            title="No Service Bus sources"
            description="Systems that publish to Azure Service Bus, such as Finance and Operations business events, can send events here instead of calling the API."
            action={
              canManage ? (
                <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                  Add source
                </Button>
              ) : undefined
            }
          />
        }
      />
      <SourceDialog appId={appId} open={adding} onOpenChange={setAdding} onCreated={open} />
      <SourceDialog
        appId={appId}
        source={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(undefined)}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(undefined)}
        title="Delete this source?"
        description={
          <>
            MailSmith stops receiving from{' '}
            <span className="font-mono">{deleting && entityLabel(deleting)}</span> and deletes the
            source’s inbound log. Messages stay in Service Bus.
          </>
        }
        confirmLabel="Delete source"
        destructive
        onConfirm={async () => {
          if (deleting) await del.mutateAsync(deleting.id)
        }}
      />
    </>
  )
}
